import * as React from 'react';
import moment from 'moment';
import { debounce } from 'lodash';
import { throttle } from 'lodash';
import { connect, Dispatch } from 'react-redux';

import { ICalendarAppState } from '../../redux-state';
import { IMonth, IEvent } from '../../interfaces';
import { scrollPosition } from '../../helpers/scroll-position';
import { scrollToHeading } from '../../helpers/scroll-to-heading';
import { updateScrolledDate, openMiniCalDialog } from '../../redux-state/actions/calendar.actions';

import ListContainerView from './container.view';
import { IDatePosition } from '../../interfaces/date-position';

interface IListContainerExternalProps {

}

interface StateToProps {
    updating: boolean;
    haseventFilter: boolean;
    eventCount: number;
    filterHash: number;
    filteredEvents: IMonth<IEvent>[];
    currentDate: moment.Moment;
    currentScrolledDate: moment.Moment;
    startDate?: moment.Moment;
    selectedFromMiniCalendar: boolean;
    dateSelectedFromMiniCalendar: moment.Moment;
}

interface DispatchToProps {
    updateScrolledDate: (to: moment.Moment) => void;
    openMiniCalDialog: (trigger: HTMLButtonElement) => void;
}

interface IListContainerState {
    loadingMore: boolean;
    datePositions: IDatePosition[];
}

export type ListContainerProps = IListContainerExternalProps & StateToProps & DispatchToProps;

/**
 * Wrapper for the list view of the calendar.
 */
class ListContainerBase extends React.Component<ListContainerProps, IListContainerState> {
    private throttledScroll: any;
    private debouncedResize: any;
    constructor(props: ListContainerProps) {
        super(props);
        this.state = {
            loadingMore: false,
            datePositions: []
        };

        // scroll and resize handlers
        this.onScroll = this.onScroll.bind(this);
        this.updateDatePositions = this.updateDatePositions.bind(this);
        this.throttledScroll = throttle(this.onScroll, 250);
        this.debouncedResize = debounce(this.updateDatePositions, 250);

        // handle focus if LoadMore is clicked
        this.loadMoreSideEffect = this.loadMoreSideEffect.bind(this);
        this.populateAndOpenSmartSeatModal = this.populateAndOpenSmartSeatModal.bind(this);
    }

    /**
     * Bind scroll/resize listeners on mount
     */
    componentDidMount() {
        document.addEventListener('scroll', this.throttledScroll);
        window.addEventListener('resize', this.debouncedResize);
    }

    /**
     * Unbind scroll/resize listeners on unmount
     */
    componentWillUnmount() {
        document.removeEventListener('scroll', this.throttledScroll);
        window.removeEventListener('resize', this.debouncedResize);
    }

    /**
     * Listen to scroll events to update sticky-nav
     */
    onScroll() {
        const dateString = this.getDateFromScrollPosition();
        if (dateString !== this.props.currentScrolledDate.format()) {
            this.props.updateScrolledDate(moment(dateString));
        }
    }

    /**
     * Get the month (as `YYYY-MM` representation) based on scroll position
     */
    getDateFromScrollPosition(): string {
        const scrollPos = scrollPosition();
        for (let i = 0; i < this.state.datePositions.length; i++) {
            const date = this.state.datePositions[i];
            const adjustedOffset = date.offsetTop - 144;
            if (adjustedOffset < scrollPos && scrollPos <= adjustedOffset + date.height) {
                return date.dateString;
            }
        }
        return this.props.currentScrolledDate.format();
    }

    /**
     * Only re-render when the number of events changes or filters change.
     * 
     * Handles scrollToDate when mini-cal date is clicked.
     */
    shouldComponentUpdate(nextProps: ListContainerProps) {
        const newFilteredMonth = (!!this.props.filteredEvents && this.props.filteredEvents.length > 0) ? !this.props.filteredEvents[0].monthMoment.isSame(nextProps.filteredEvents[0].monthMoment) : false;

        if (!newFilteredMonth && !this.props.haseventFilter && this.props.selectedFromMiniCalendar != nextProps.selectedFromMiniCalendar && this.props.dateSelectedFromMiniCalendar
            != nextProps.dateSelectedFromMiniCalendar)
            return true;


        return !nextProps.currentDate.isSame(this.props.currentDate) ||
            this.props.eventCount !== nextProps.eventCount ||
            this.props.filterHash !== nextProps.filterHash ||
            this.props.currentScrolledDate !== nextProps.currentScrolledDate ||
            newFilteredMonth;
    }

    /**
     * Handle DOM-changes after re-render
     */
    componentDidUpdate(oldProps: ListContainerProps) {
        this.updateDatePositions();
        this.props.updateScrolledDate(this.props.currentDate);

        if (this.state.loadingMore) {
            this.setState({
                loadingMore: false
            }, () => { });

        } else {
            scrollToHeading(this.props.currentDate, (this.props.currentDate.isSameOrBefore(this.props.currentDate.clone().startOf('month'))));
        }
    }

    /**
     * Update the state's references of month positions
     */
    updateDatePositions() {

        // compile position information into an array
        const datePositions: IDatePosition[] = [];
        const dateItems = Array.prototype.slice.call(document.querySelectorAll('[aria-labelledby*="List-Day-"]')) as HTMLLIElement[];
        dateItems.forEach(dateItem => {
            datePositions.push({
                dateString: dateItem.getAttribute('data-date-moment'),
                offsetTop: dateItem.offsetTop,
                height: dateItem.offsetHeight
            });
        });

        // update the state
        this.setState({
            datePositions
        }, () => {
            // update sticky-nav if scrolled month changed
            this.onScroll();
        });
    }

    /**
     * Internal state to account for `LoadMore`-specific focus behavior
     */
    loadMoreSideEffect() {
        this.setState({
            loadingMore: true
        });
    }

    populateAndOpenSmartSeatModal(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        const reserveUrl = (event.target as HTMLButtonElement).getAttribute('data-href');
        if (reserveUrl) {
            const intermediaryLink = document.querySelector('#SmartSeatModal_trigger') as HTMLButtonElement;
            intermediaryLink.setAttribute('data-href', reserveUrl);
            intermediaryLink.click();
        }
    }

    render() {
        return (
            <ListContainerView {...this.props} loadMoreSideEffect={this.loadMoreSideEffect} />
        )
    }
}

function mapStateToProps(state: ICalendarAppState, ownProps: IListContainerExternalProps): StateToProps {
    let eventCount = 0;

    state.calendar.filteredEvents.forEach(month => {
        month.events.days.forEach(day => {
            eventCount += day.events.length;
        });
    });
    console.log("--------------------------")
    console.log(state.calendar.filteredEvents)
    console.log("--------------------------")

    return  {
        updating: state.calendar.updating,
        eventCount,
        filterHash: state.calendar.filterHash,
        currentDate: state.calendar.currentDate,
        startDate: moment(state.calendar.startupState.startDate).clone().startOf('day'),
        filteredEvents: state.calendar.filteredEvents,
        currentScrolledDate: state.calendar.currentScrolledDate,
        selectedFromMiniCalendar: state.calendar.miniCalendarDateSelected,
        haseventFilter: state.calendar.eventFilter.length > 0,
        dateSelectedFromMiniCalendar: state.calendar.miniCalendarDate
    }
    
}

function mapDispatchToProps(dispatch: Dispatch): DispatchToProps {
    return {
        updateScrolledDate: (to: moment.Moment) => { dispatch(updateScrolledDate(to)); },
        openMiniCalDialog: (trigger: HTMLButtonElement) => { dispatch(openMiniCalDialog(trigger)); }
    }
}

export default connect<StateToProps, DispatchToProps>(mapStateToProps, mapDispatchToProps)(ListContainerBase);
